import { inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { ReplaySubject, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import { coreSelectors } from 'app/core/state';
import User from 'entity/User';
import { WebsocketService } from '../websocket.service';
import { HeaderToastMessageService } from '../header-toast-message.service';
import { SceneService } from '../scene.service';
import { AppState } from 'app/types';

export enum ScreenShareStatus {
  NONE = 'none',
  ACTIVE = 'active',
  LOADING = 'loading',
  ERROR = 'error',
}

/**
 * Base service for group screen sharing functionality.
 * Provides common properties and methods used by the specialized services.
 */
export abstract class GroupShareScreenBaseService extends Loggable {
  protected readonly store = inject<Store<AppState>>(Store);
  protected readonly websocketService = inject(WebsocketService);
  protected readonly headerToastMessageService = inject(HeaderToastMessageService);
  protected readonly sceneService = inject(SceneService);

  protected readonly userSignal = signal<User | undefined>(undefined);
  protected readonly userId = signal<string | undefined>(undefined);
  protected readonly screenSharingUserId = signal<string | undefined>(undefined);
  protected readonly isGroupShareActive = signal<boolean>(false);
  protected readonly screenShareStatus = signal<ScreenShareStatus>(ScreenShareStatus.NONE);
  public readonly isHost = signal<boolean>(false);

  protected destroyed$ = new ReplaySubject<boolean>(1);

  protected constructor() {
    super();

    // Common initialization - subscribe to user state
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ user }) => {
        this.userSignal.set(user);
        this.userId.set(user?.userId);
      });
  }

  /**
   * Clean up subscriptions and resources.
   */
  protected cleanUp(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /**
   * Display a banner message to the user.
   * @param message The message to display
   * @param durationInSeconds Duration to show the message (default: 5 seconds)
   */
  protected displayBannerMessage(message: string, durationInSeconds = 5): void {
    this.headerToastMessageService.pushTemporaryMessage({
      message,
      durationInSeconds,
    });
  }

  /**
   * Update the host status based on the current user and screen sharing user
   */
  protected updateHostStatus(): void {
    const isHost = this.screenSharingUserId() === this.userId();
    this.isHost.set(isHost);
  }

  /**
   * Safely cleans up media stream tracks
   */
  protected cleanupMediaStream(stream?: MediaStream): void {
    if (!stream) {
      return;
    }

    try {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    } catch (error) {
      this.error('Error cleaning up media stream', { error });
    }
  }

  /**
   * Update screen share status with logging
   */
  protected setScreenShareStatus(status: ScreenShareStatus, logInfo?: object): void {
    const prevStatus = this.screenShareStatus();
    this.screenShareStatus.set(status);

    if (prevStatus !== status) {
      this.info(`Screen share status changed from ${prevStatus} to ${status}`, logInfo);
    }
  }
}
